import {
  ICombinationMedia,
  SlideMedia,
  ProductCombinationMedias,
  ProductMedia,
  ProductMedias,
  HeroCarouselMedias,
  IVideo,
} from '../../../../types/interfaces';

import { buildCloudinaryFetchUrl } from './cloudinaryFetch';
import {
  CommonCloudinaryTransformer,
  PosterImageCloudinaryTransformer,
  ReferentialCloudinaryTransformer,
} from './CloudinaryTransformer';
import { ThumbnailWidth, GalleryWidth, ZoomWidth } from './productPageDimensions';

export abstract class BaseMediaTransformer {
  constructor() {}
  abstract transformProductPageMedias(productPageMedias: ProductMedias): HeroCarouselMedias;

  protected transformDocumentCombinationMedia(
    combinationMedia: ProductCombinationMedias
  ): ICombinationMedia {
    return {
      combination: combinationMedia.combination,
      medias: combinationMedia.medias.map((media) => this.transformDocumentMedia(media)),
    };
  }
  private transformDocumentMedia(media: ProductMedia): SlideMedia {
    if (media.video) {
      return {
        video: {
          id: media.video.publicId,
          deliveryUrl: media.video.deliveryUrl,
          cloudinaryUrl: media.video.cloudinaryUrl,
          altText: media.video.altText,
          autoplay: media.video.autoplay,
          loop: media.video.loop,
          name: media.video.videoName,
          description: media.video.videoDescription,
          posterImage: media.video.posterImage,
          smallPosterImage: media.video.smallPosterImageFrame,
        },
      };
    }

    return {
      image: {
        thumbnail: this.updateWidth(media.image.url, ThumbnailWidth),
        gallery: this.updateWidth(media.image.url, GalleryWidth),
        zoom: this.updateWidth(media.image.url, ZoomWidth),
        altText: media.image.altText,
      },
    };
  }

  private updateWidth(url: string, width: number): string {
    const parsedUrl = new URL(url);
    parsedUrl.searchParams.set('width', String(width));
    return parsedUrl.toString();
  }
}

export class UrlFetchCloudinaryTransformer extends BaseMediaTransformer {
  public transformProductPageMedias(productPageMedias: ProductMedias): HeroCarouselMedias {
    return {
      common: productPageMedias.common.map((media) => this.transformMedia(media)),
      combinationMedias: productPageMedias.combinationMedias.map((combinationMedia) =>
        this.transformCombinationMedia(combinationMedia)
      ),
      documentCombinationMedias: productPageMedias.documentCombinationMedias?.map(
        (combinationMedia) => this.transformDocumentCombinationMedia(combinationMedia)
      ),
    };
  }
  private transformCombinationMedia(combinationMedia: ProductCombinationMedias): ICombinationMedia {
    return {
      combination: combinationMedia.combination,
      medias: combinationMedia.medias.map((media) => this.transformMedia(media)),
    };
  }

  private transformMedia(media: ProductMedia): SlideMedia {
    if (media.video) {
      return {
        video: {
          id: media.video.publicId,
          deliveryUrl: media.video.deliveryUrl,
          cloudinaryUrl: media.video.cloudinaryUrl,
          altText: media.video.altText,
          autoplay: media.video.autoplay,
          loop: media.video.loop,
          name: media.video.videoName,
          description: media.video.videoDescription,
          posterImage: media.video.posterImage,
          smallPosterImage: media.video.smallPosterImageFrame,
        },
      };
    }

    return {
      image: { ...this.transformImage(media.image!.url), altText: media.image.altText },
    };
  }

  private transformImage(image: string): {
    thumbnail: string;
    gallery: string;
    zoom: string;
    altText?: string;
  } {
    return {
      thumbnail: this.transformUrl(image, ThumbnailWidth),
      gallery: this.transformUrl(image, GalleryWidth),
      zoom: this.transformUrl(image, ZoomWidth),
    };
  }

  private transformUrl(url: string, width: number): string {
    return buildCloudinaryFetchUrl(url, width);
  }
}

export class CloudinaryMediaTransformer extends BaseMediaTransformer {
  public transformProductPageMedias(productPageMedias: ProductMedias): HeroCarouselMedias {
    return {
      common: this.buildCommonMedias(productPageMedias.common),
      combinationMedias: this.buildReferentialMedias(productPageMedias.combinationMedias),
      documentCombinationMedias: productPageMedias.documentCombinationMedias?.map(
        (combinationMedia) => this.transformDocumentCombinationMedia(combinationMedia)
      ),
    };
  }

  private transformVideo(media: { video: IVideo; image?: never }): SlideMedia {
    const cloudinaryTransformer = new PosterImageCloudinaryTransformer(media.video.posterImage);
    return {
      video: {
        id: media.video.publicId,
        deliveryUrl: media.video.deliveryUrl,
        cloudinaryUrl: media.video.cloudinaryUrl,
        altText: media.video.altText,
        autoplay: media.video.autoplay,
        loop: media.video.loop,
        name: media.video.videoName,
        description: media.video.videoDescription,
        posterImage: cloudinaryTransformer.isValid
          ? cloudinaryTransformer.gallery
          : media.video.defaultPosterImage,
        smallPosterImage: cloudinaryTransformer.isValid
          ? cloudinaryTransformer.thumbnail
          : media.video.smallPosterImageFrame,
      },
    };
  }
  private buildCommonMedias(heroGalleryImages: ProductMedia[]): SlideMedia[] {
    const resultingImages: SlideMedia[] = heroGalleryImages.reduce(
      (heroGalleryMedias: SlideMedia[], media: ProductMedia) => {
        if (media.image) {
          const cloudinaryTransformer = new CommonCloudinaryTransformer(media.image);
          if (cloudinaryTransformer.isValid) {
            if (media.image.url.includes('t_pdpHeroGallery')) {
              // Native Cloudinary image: use named transformations (t_pdpHeroGallery_*)
              heroGalleryMedias.push({
                image: {
                  thumbnail: cloudinaryTransformer.thumbnail,
                  gallery: cloudinaryTransformer.gallery,
                  zoom: cloudinaryTransformer.zoom,
                  altText: media.image.altText,
                },
              });
            } else {
              // Non-native image (e.g. ad landing feed/template image): proxy through
              // Cloudinary fetch. TransformerFactory selects this transformer based on
              // combinationMedias containing t_pdpHeroGallery, so common images may be
              // from a different origin (e.g. rendering.mcp.cimpress.com).
              heroGalleryMedias.push({
                image: {
                  thumbnail: buildCloudinaryFetchUrl(media.image.url, ThumbnailWidth),
                  gallery: buildCloudinaryFetchUrl(media.image.url, GalleryWidth),
                  zoom: buildCloudinaryFetchUrl(media.image.url, ZoomWidth),
                  altText: media.image.altText,
                },
              });
            }
          }
        } else if (media.video) {
          heroGalleryMedias.push(this.transformVideo(media));
        }

        return heroGalleryMedias;
      },
      []
    );
    return resultingImages;
  }

  private buildReferentialMedias(
    combinationMedias: ProductCombinationMedias[]
  ): ICombinationMedia[] {
    const resultingRefImages = [];
    for (const combinationMedia of combinationMedias) {
      const tranformedMedias = combinationMedia.medias.map((media) => {
        if (media.video) {
          return this.transformVideo(media);
        }
        const cloudinaryTransformer = new ReferentialCloudinaryTransformer(media.image!);
        return {
          image: {
            thumbnail: cloudinaryTransformer.thumbnail,
            gallery: cloudinaryTransformer.gallery,
            zoom: cloudinaryTransformer.zoom,
            altText: media.image?.altText,
          },
        };
      });
      resultingRefImages.push({
        combination: combinationMedia.combination,
        medias: tranformedMedias,
      });
    }

    return resultingRefImages;
  }
}
